import requestApi from '../../config';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IResponseApi, ListTreeResponse, TreeViewEnum, UserInfoResponse, ProviderAccess, RawProviderData, ProviderVendorAccess, RawProviderCategory, ERR_MSG } from '@/interfaces';
import { agentDetail } from '@/pages/agents/store/agentSlice';
import { useAppDispatch } from '@/hooks/hooks';
import { showError } from '@/utils/alert';
import { useTranslation } from 'react-i18next';

const URL_LIST_TREE = '/auth-service/agents/tree';
const URL_DETAIL_AGENT = `/auth-service/v2/agent/details`;
const URL_PROVIDER_VENDOR_ACCESS = {
  GET: (agent_id: string | undefined) => `/adapter-service/providers/vendor-status/${agent_id}`,
  UPDATE: (agent_id: string | undefined) => `/adapter-service/providers/vendor-status/${agent_id}`
}

export const useListTree = () => {
  return useQuery<ListTreeResponse, IResponseApi>({
    queryKey: [TreeViewEnum.LIST_TREE_DATA],
    queryFn: async () => {
      const { data } = await requestApi.get<ListTreeResponse>(URL_LIST_TREE);
      return data;
    },
  });
};
export const useDetailAgent = () => {
  const dispatch = useAppDispatch();
  return useMutation<UserInfoResponse, IResponseApi, string | number, number>(
    async (id) => {
      const { data } = await requestApi.get<UserInfoResponse>(URL_DETAIL_AGENT, { id });
      return data;
    },
    {
      onSuccess(data) {
        if (data) dispatch(agentDetail(data));
      },
    },
  );
};

export const useUserInfo = () => {
  return useMutation<UserInfoResponse, IResponseApi, string | number, number>(async (id) => {
    const url = `/user-info/${id}`;
    const { data } = await requestApi.get<UserInfoResponse>(url);
    return data;
  });
};

export const useProviderAccessInfoV2 = () => {
  return useMutation(
    async (params: string | undefined) => {
      const res = await requestApi.get<ProviderVendorAccess[]>(URL_PROVIDER_VENDOR_ACCESS.GET(params));

      const convertedData: RawProviderData = transformFlatDataToHierarchical(res.data);

      return { ...res, data: convertedData };
    }
  );
};

const transformFlatDataToHierarchical = (flatData: ProviderVendorAccess[]): RawProviderData => {
  const groupedByAggregator = new Map<string, ProviderVendorAccess[]>();

  flatData.forEach(item => {
    if (!groupedByAggregator.has(item.agrgId)) {
      groupedByAggregator.set(item.agrgId, []);
    }
    groupedByAggregator.get(item.agrgId)!.push(item);
  });

  const hierarchicalData: RawProviderData = [];

  groupedByAggregator.forEach((providers, aggregatorId) => {
    providers.sort((a, b) => a.vendorCode.localeCompare(b.vendorCode));

    const aggregatorStatus = providers.some(p => p.status);

    hierarchicalData.push({
      aggregatorId: aggregatorId,
      status: aggregatorStatus,
      children: providers.map(provider => ({
        providerId: provider.prvId,
        vendorCode: provider.vendorCode,
        status: provider.status
      }))
    });
  });

  hierarchicalData.sort((a, b) => a.aggregatorId.localeCompare(b.aggregatorId));

  return hierarchicalData;
};

export const useUpdateProviderAccessV2 = (agentId: string | undefined) => {
  const { t } = useTranslation();
  return useMutation(
    async (data: Partial<ProviderVendorAccess>[]) => {      
      const res = await requestApi.post(URL_PROVIDER_VENDOR_ACCESS.UPDATE(agentId), data);
      return res;
    },
    {
      onError: (e: IResponseApi) => {
        if (e?.message === ERR_MSG.VENDOR_SETTING_UPDATE_STATE) {
          return showError(t('agents.changeAccessControlForm.alert_not_met'));
        }
        showError();
      },
    },
  );
};